/**
 * Générateur — Automatisme : trouver un dénominateur commun à deux fractions
 * pour les additionner, les soustraire ou les comparer.
 * Attendu ID : 1223 | Niveau : 3ème | Domaine : Multiples et diviseurs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 787).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTENSION de gen_1097 (4e, « Le même dénominateur d'abord ») : mêmes
 * tirages (cas multiples, premiers, quelconque de 1054/1097, mêmes
 * sacrifiés importés), mais LA RÉPONSE EST LE DÉNOMINATEUR (un nombre),
 * jamais le résultat (1213) ni le symbole de comparaison (1098). Les cartes
 * meme_* de 1097 re-badgées telles quelles demanderaient le résultat : ce
 * serait une redite de 1213 ; ici QCM et Vrai/Faux propres au dénominateur
 * (choix mode auto, À ARBITRER). La finalité (additionner, soustraire,
 * comparer) vit dans contenu.sousType : même réponse.
 * « UN dénominateur commun » : tout multiple commun convient (le produit
 * aussi : jamais un distracteur) ; « le plus petit » n'est exigé qu'en
 * saisie (unicité, comme gen_700 « le plus petit chiffre » ; À ARBITRER).
 * Commun ≤ 30 (1097). Banque MathsMentales (S. Cogez, Apache 2.0, relevé
 * 2026-09-12) : aucune activité ne demande le dénominateur seul (5NC7/5NC8
 * calculent, 6NB3 compare) ; le format vient du programme, les dénominateurs
 * (2..10, multiples ou quelconques) de 5NC7 option 3.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes « Pour
 *     5/6 + 3/4, le plus petit dénominateur commun est ⬚ » (une en finalité
 *     comparer : « Pour comparer 2/5 et 1/4, … »), cas multiples, premiers,
 *     quelconque × 2, dénominateurs communs distincts.
 *   Carte 2 « Le même dénominateur d'abord » (commun_qcm_unique) : « Pour
 *     calculer 5/6 + 3/4, un dénominateur commun est… », QUATRE nombres
 *     [12 ; 10 ; 6 ; 18] (6 < 10 < 12 < 18 : la clé encadrée quand les
 *     familles le permettent), aucun distracteur n'est un multiple commun.
 *   Carte 3 « Vrai ou faux » (commun_tableau_vf) : quatre lignes « Pour
 *     5/6 + 3/4, 24 convient. » (V : un commun n'est pas forcément le plus
 *     petit) ; « …, 10 convient. » (F) ; vérité = multiple des deux.
 *
 * INVARIANTS : le dénominateur commun est celui de l'item (recalculé : ppcm,
 * throw si ≠) ; tout distracteur est un entier ≥ 2 qui n'est PAS multiple
 * commun des deux dénominateurs ; les lignes vraies du V/F sont des
 * multiples communs ≤ 60.
 * SACRIFIÉS : ceux de 1054 et 1097 (importés : 5/6 avec 3/4 ne sort jamais),
 * rien de plus (la fiche reprend la page « Un nombre des deux tables »).
 *
 * ── Erreurs (familles nommées ; DEPP 2de 2025 q.8 « 4/5 − 1/3 ») ────────
 *   bas_additionnes    : 10 pour 6 et 4 (3/8 = 4,9 %), PRINCIPALE ;
 *   bas_soustraits     : 2 pour 5 et 3 (3/2 = 23,3 % : aucun commun cherché) ;
 *   grand_denominateur : 6 pour 6 et 4 (nom de 1097 ; seulement quand il
 *                        n'est pas multiple de l'autre, sinon c'est la clé) ;
 *   multiple_d_un_seul : 18 pour 6 et 4 (dans une seule table), NEUF ;
 *   petit_denominateur : 4 pour 6 et 4.
 */
import {
    randomChoice, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { tirerItem, item, fracTex, gcd, REPLIS as REPLIS_1054 } from '../../5eme/nombres-rationnels/gen_1054';
import { tirerQuelconque } from '../../4eme/nombres-rationnels/gen_1097';

const ATTENDU_ID = 1223;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Le même dénominateur d\'abord';
const L_VF = 'Vrai ou faux';

// ─── Le domaine (les items de 1054/1097, le dénominateur en réponse) ──────

const ppcm = (b, d) => (b * d) / gcd(b, d);
/** Un item d'un cas, avec son dénominateur commun vérifié (throw si ≠ ppcm) ; null si le tirage échoue. */
const tirer = (cas, contraintes = {}) => {
    const it = cas === 'quelconque' ? tirerQuelconque(contraintes) : tirerItem(cas, contraintes);
    if (!it) return null;
    if (it.den !== ppcm(it.g.b, it.d.b)) throw new Error(`gen_1223 : commun ${it.den} ≠ ppcm(${it.g.b}, ${it.d.b})`);
    return it;
};
const REPLIS = {
    multiples: REPLIS_1054.multiples,
    premiers: REPLIS_1054.premiers,
    quelconque: item('quelconque', '+', { a: 1, b: 4 }, { a: 1, b: 6 }, 12),
};
const CAS = ['multiples', 'premiers', 'quelconque', 'quelconque'];

const FINALITES = ['additionner', 'soustraire', 'comparer'];
/** L'amorce : « Pour 5/6 + 3/4 » (additionner / soustraire, selon l'opération de l'item) ou « Pour comparer 2/5 et 1/4 ». */
const amorce = (it, finalite, verbe = false) => {
    // en soustraction, la plus grande fraction d'abord (la réponse ne dépend pas de l'ordre)
    const [x, y] = finalite === 'soustraire' && it.g.a * it.d.b < it.d.a * it.g.b ? [it.d, it.g] : [it.g, it.d];
    const g = fracTex(x.a, x.b); const d = fracTex(y.a, y.b);
    if (finalite === 'comparer') return `Pour comparer $${g}$ et $${d}$`;
    const op = finalite === 'additionner' ? '+' : '-';
    return verbe ? `Pour calculer $${g} ${op} ${d}$` : `Pour $${g} ${op} ${d}$`;
};

const EXPLICATIONS = {
    bas_additionnes: 'On n\'additionne pas les dénominateurs : un dénominateur commun est un nombre qui est dans la table de 6 ET dans la table de 4.',
    bas_soustraits: 'Soustraire les dénominateurs ne donne rien : il faut un nombre dans les DEUX tables.',
    grand_denominateur: 'Le plus grand dénominateur ne suffit pas toujours : 6 n\'est pas dans la table de 4. Il faut 12.',
    multiple_d_un_seul: '18 est dans la table de 6, mais pas dans celle de 4 : il faut un nombre des DEUX tables.',
    petit_denominateur: 'Le plus petit dénominateur n\'est pas dans la table de l\'autre.',
    regle: 'Un dénominateur commun, c\'est un nombre qui est dans les deux tables ; le plus petit est le plus pratique, mais tout multiple commun convient.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

const estCommun = (n, it) => n % it.g.b === 0 && n % it.d.b === 0;

/** Les candidats (entiers ≥ 2, jamais un multiple commun, jamais deux fois la même valeur). */
const candidats = (it) => {
    const b = it.g.b; const d = it.d.b;
    const [petit, grand] = b < d ? [b, d] : [d, b];
    const c = [];
    const pousser = (code, n) => { if (n >= 2 && !estCommun(n, it) && !c.some((x) => x.n === n)) c.push({ code, n }); };
    pousser('bas_additionnes', b + d);
    pousser('bas_soustraits', grand - petit);
    pousser('grand_denominateur', grand);
    pousser('petit_denominateur', petit);
    for (const k of shuffle([2, 3, 4])) { pousser('multiple_d_un_seul', k * grand); pousser('multiple_d_un_seul', k * petit); }
    return c;
};

/** Trois distracteurs de familles distinctes, bas_additionnes en premier s'il existe ; null s'il en manque. */
const distracteurs = (it) => {
    const cands = candidats(it);
    const pris = [];
    for (const x of [...cands.filter((x) => x.code === 'bas_additionnes'), ...shuffle(cands.filter((x) => x.code !== 'bas_additionnes'))]) {
        if (!pris.some((y) => y.code === x.code)) pris.push(x);
    }
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = CAS.map((cas) => tirer(cas));
        if (items.some((it) => !it) || new Set(items.map((it) => it.den)).size < 4) continue;
        const finalites = shuffle(['comparer', ...items.slice(1).map((it) => (it.op === '+' ? 'additionner' : 'soustraire'))]);
        const phrase = items.map((it, i) => `${amorce(it, finalites[i])}, le plus petit dénominateur commun est {{${it.den}}}.`).join('\n');
        const payload = makeTexteTrou(phrase, '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, bas_additionnes: EXPLICATIONS.bas_additionnes } };
        payload._meta.params = { items, finalites };
        return payload;
    }
    const items = [REPLIS.multiples, REPLIS.premiers, REPLIS.quelconque, item('quelconque', '-', { a: 5, b: 6 }, { a: 1, b: 4 }, 12) || REPLIS.quelconque];
    const finalites = ['soustraire', 'additionner', 'comparer', 'soustraire'];
    const phrase = items.map((it, i) => `${amorce(it, finalites[i])}, le plus petit dénominateur commun est {{${it.den}}}.`).join('\n');
    const payload = makeTexteTrou(phrase, '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items, finalites };
    return payload;
};

// ─── Carte 2 : QCM ────────────────────────────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const it = tirer(randomChoice(CAS));
        if (!it) continue;
        const faux = distracteurs(it);
        if (!faux) continue;
        const finalite = randomChoice(FINALITES.filter((f) => f === 'comparer' || (f === 'additionner') === (it.op === '+')));
        const bon = String(it.den);
        const choix = [bon, ...faux.map((f) => String(f.n))];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, bon, `<p><b>${amorce(it, finalite, true)}, un dénominateur commun est…</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, sousType: finalite, explications: EXPLICATIONS };
        payload._meta.params = { ...it, finalite, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 1/4 + 1/6 → 12 ; 10 (bas_additionnes), 6 (grand_denominateur), 18 (multiple_d_un_seul).
    const it = REPLIS.quelconque;
    const payload = makeQcm(['12', '10', '6', '18'], '12', `<p><b>${amorce(it, 'additionner', true)}, un dénominateur commun est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: 'additionner', explications: EXPLICATIONS };
    payload._meta.params = { ...it, finalite: 'additionner', distracteurs: [{ code: 'bas_additionnes' }, { code: 'grand_denominateur' }, { code: 'multiple_d_un_seul' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux ───────────────────────────────────────────────

/** « Pour …, n convient. » : vraie avec un multiple commun ≤ 60 (le plus petit une fois sur deux), fausse par une famille. */
const ligneVf = (it, vraie) => {
    const finalite = randomChoice(FINALITES.filter((f) => f === 'comparer' || (f === 'additionner') === (it.op === '+')));
    if (vraie) {
        const multiples = [1, 2, 3, 4, 5].map((k) => k * it.den).filter((n) => n <= 60);
        const n = randomChoice([it.den, ...multiples]);
        return { ligne: `${amorce(it, finalite)}, ${n} convient.`, code: null, n };
    }
    const cands = candidats(it);
    if (!cands.length) return null;
    const x = randomChoice(cands);
    return { ligne: `${amorce(it, finalite)}, ${x.n} convient.`, code: x.code, n: x.n };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const items = CAS.map((cas) => tirer(cas));
        if (items.some((it) => !it) || new Set(items.map((it) => it.den)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = items.map((it, i) => ligneVf(it, verites[i]));
        if (lignes.some((l) => !l) || new Set(lignes.map((l) => l.ligne)).size < 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 4/7 − 2/21 → 21 V ; 1/2 + 1/5 → 7 F (bas_additionnes) ; comparer 1/4 et 1/6 → 24 V ; 1/4 + 1/6 → 6 F (grand_denominateur).
    const lignes = [
        `${amorce(REPLIS.multiples, 'soustraire')}, 21 convient.`,
        `${amorce(REPLIS.premiers, 'additionner')}, 7 convient.`,
        `${amorce(REPLIS.quelconque, 'comparer')}, 24 convient.`,
        `${amorce(REPLIS.quelconque, 'additionner')}, 6 convient.`,
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: [REPLIS.multiples, REPLIS.premiers, REPLIS.quelconque, REPLIS.quelconque], verites: [true, false, true, false], distracteurs: [{ code: 'bas_additionnes' }, { code: 'grand_denominateur' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre nombres entiers : la clé (le plus petit dénominateur commun) et trois familles distinctes, bas_additionnes en premier ; aucun distracteur n\'est un multiple commun (le produit b × d convient : jamais distracteur)',
        'items de 1054/1097 (cas multiples, premiers, quelconque), sacrifiés importés',
    ],
    qcm_unique: {
        support: 'dénominateurs 2..10 multiples ou quelconques, commun ≤ 30 ; contenu.sousType = additionner / soustraire / comparer',
        familles: {
            bas_additionnes: 'b + d (DEPP 2de 2025 q.8 : 3/8, 4,9 %), la principale',
            bas_soustraits: 'grand − petit (q.8 : 3/2, 23,3 %)',
            grand_denominateur: 'le plus grand dénominateur quand il n\'est pas multiple de l\'autre (cas premiers et quelconque)',
            multiple_d_un_seul: 'k × un seul dénominateur, hors de l\'autre table',
            petit_denominateur: 'le plus petit dénominateur',
        },
    },
    vf_tableau_vf: 'quatre items, quatre communs distincts ; les lignes vraies prennent un multiple commun ≤ 60 (le plus petit une fois sur deux) ; les fausses une famille tirée',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    commun_qcm_unique: genQcm,
    commun_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'commun', label: L_QCM, formatsList: [
        { key: 'commun_qcm_unique', label: 'QCM' },
        { key: 'commun_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
